import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep, get } from 'lodash';

import type { MaasGroupSpec, MaasSharedFormValues } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  parseNodesField,
  syncInferenceSystemArchitectureFromNode,
  writeNodesField,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS,
  type MaasNodeGroupFieldPaths,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupFieldPaths';
import { validateNodeGroupResource } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupValidation';
import { useScheduleResourceFormPriceRefresh } from '@/delivery-type-plugin/platform/resource-form';

import type { GroupRole } from '../group-spec-modal';

function readNodesFromForm(form: FormInstance, paths: MaasNodeGroupFieldPaths) {
  return {
    masterNodes: parseNodesField(form.getFieldValue(paths.masterNodes)),
    workerNodes: parseNodesField(form.getFieldValue(paths.workerNodes)),
  };
}

function readNodesFromInitValues(initValues: MaasSharedFormValues | undefined, paths: MaasNodeGroupFieldPaths) {
  return {
    masterNodes: parseNodesField(get(initValues, paths.masterNodes)),
    workerNodes: parseNodesField(get(initValues, paths.workerNodes)),
  };
}

/** 详情接口回填的节点快照，编辑态切回原始部署模式时还原。 */
function buildDetailNodeSnapshot(initValues: MaasSharedFormValues | undefined, paths: MaasNodeGroupFieldPaths) {
  const runMode = get(initValues, paths.runMode) as MaasRunMode | undefined;
  if (!runMode) return undefined;
  const nodes = readNodesFromInitValues(initValues, paths);
  return {
    runMode,
    masterNodes: cloneDeep(nodes.masterNodes),
    workerNodes: cloneDeep(nodes.workerNodes),
    masterSystemDiskSize: get(initValues, paths.masterSystemDisk) as number | undefined,
    workerSystemDiskSize: get(initValues, paths.workerSystemDisk) as number | undefined,
  };
}

function clearNodeFieldsOnForm(form: FormInstance, paths: MaasNodeGroupFieldPaths) {
  form.setFieldValue(paths.masterSystemDisk, undefined);
  form.setFieldValue(paths.workerSystemDisk, undefined);
  form.setFieldValue(paths.masterSystemDiskPricing, undefined);
  form.setFieldValue(paths.workerSystemDiskPricing, undefined);
  form.setFields({
    [paths.masterNodes]: { error: undefined },
    [paths.workerNodes]: { error: undefined },
  });
}

interface UseGroupsConfigStateOptions {
  form: FormInstance;
  runMode: MaasRunMode | undefined;
  isEdit?: boolean;
  initValues?: MaasSharedFormValues;
  onRunModeChange?: (form: FormInstance, mode: MaasRunMode) => void;
  fieldPaths?: MaasNodeGroupFieldPaths;
}

/** 节点组状态编排：负责 runMode 切换、节点 JSON 同步与弹窗状态。 */
export function useGroupsConfigState({
  form,
  runMode,
  isEdit,
  initValues,
  onRunModeChange,
  fieldPaths = DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS,
}: UseGroupsConfigStateOptions) {
  const paths = fieldPaths;
  const initialNodes = useMemo(
    () => (initValues ? readNodesFromInitValues(initValues, paths) : readNodesFromForm(form, paths)),
    [form, initValues, paths],
  );
  const detailNodeSnapshot = useMemo(() => buildDetailNodeSnapshot(initValues, paths), [initValues, paths]);

  const [masterNodes, setMasterNodes] = useState<MaasGroupSpec[]>(() => initialNodes.masterNodes);
  const [workerNodes, setWorkerNodes] = useState<MaasGroupSpec[]>(() => initialNodes.workerNodes);
  const [nodeModalVisible, setNodeModalVisible] = useState(false);
  const [modalRole, setModalRole] = useState<GroupRole>('worker');
  const [editingIndex, setEditingIndex] = useState(-1);
  const masterNodesRaw = Form.useWatch(paths.masterNodes, form);
  const workerNodesRaw = Form.useWatch(paths.workerNodes, form);
  const skipExternalNodesSyncRef = useRef(true);
  const isNormal = runMode === MaasRunMode.Normal;
  const isPrimarySecondary = runMode === MaasRunMode.PrimarySecondary;
  const schedulePriceRefresh = useScheduleResourceFormPriceRefresh();

  const syncNodesToForm = useCallback(
    (masters: MaasGroupSpec[], workers: MaasGroupSpec[]) => {
      writeNodesField(form, paths.masterNodes, masters);
      writeNodesField(form, paths.workerNodes, workers);
    },
    [form, paths.masterNodes, paths.workerNodes],
  );

  useEffect(() => {
    const initMaster = get(initValues, paths.masterNodes);
    const initWorker = get(initValues, paths.workerNodes);
    if (initMaster != null || initWorker != null) {
      const masters = parseNodesField(initMaster);
      const workers = parseNodesField(initWorker);
      setMasterNodes(masters);
      setWorkerNodes(workers);
      syncNodesToForm(masters, workers);
      return;
    }
    const fromForm = readNodesFromForm(form, paths);
    if (fromForm.masterNodes.length || fromForm.workerNodes.length) {
      setMasterNodes(fromForm.masterNodes);
      setWorkerNodes(fromForm.workerNodes);
    }
  }, [form, initValues, paths, syncNodesToForm]);

  useEffect(() => {
    if (skipExternalNodesSyncRef.current) {
      skipExternalNodesSyncRef.current = false;
      return;
    }
    setMasterNodes(parseNodesField(masterNodesRaw));
    setWorkerNodes(parseNodesField(workerNodesRaw));
  }, [masterNodesRaw, workerNodesRaw]);

  const handleRunModeChange = useCallback(
    (mode: MaasRunMode) => {
      if (mode === runMode) return;
      form.setFieldValue(paths.runMode, mode);
      onRunModeChange?.(form, mode);

      const restoreFromDetail = !!(isEdit && detailNodeSnapshot && detailNodeSnapshot.runMode === mode);
      if (restoreFromDetail) {
        setMasterNodes(detailNodeSnapshot.masterNodes);
        setWorkerNodes(detailNodeSnapshot.workerNodes);
        syncNodesToForm(detailNodeSnapshot.masterNodes, detailNodeSnapshot.workerNodes);
        if (detailNodeSnapshot.masterSystemDiskSize !== undefined) {
          form.setFieldValue(paths.masterSystemDisk, detailNodeSnapshot.masterSystemDiskSize);
        } else {
          form.setFieldValue(paths.masterSystemDisk, undefined);
          form.setFieldValue(paths.masterSystemDiskPricing, undefined);
        }
        if (detailNodeSnapshot.workerSystemDiskSize !== undefined) {
          form.setFieldValue(paths.workerSystemDisk, detailNodeSnapshot.workerSystemDiskSize);
        } else {
          form.setFieldValue(paths.workerSystemDisk, undefined);
          form.setFieldValue(paths.workerSystemDiskPricing, undefined);
        }
        form.setFields({
          [paths.masterNodes]: { error: undefined },
          [paths.workerNodes]: { error: undefined },
        });
        return;
      }

      setMasterNodes([]);
      setWorkerNodes([]);
      syncNodesToForm([], []);
      clearNodeFieldsOnForm(form, paths);
    },
    [detailNodeSnapshot, form, isEdit, onRunModeChange, paths, runMode, syncNodesToForm],
  );

  const openMasterModal = useCallback(() => {
    setModalRole('master');
    setEditingIndex(0);
    setNodeModalVisible(true);
  }, []);

  const openWorkerModal = useCallback((index = -1) => {
    setModalRole('worker');
    setEditingIndex(index);
    setNodeModalVisible(true);
  }, []);

  const handleNodeModalOk = useCallback(
    (values: MaasGroupSpec) => {
      const isPrimaryInferenceNode =
        runMode === MaasRunMode.PrimarySecondary && modalRole === 'master'
          ? true
          : runMode !== MaasRunMode.PrimarySecondary && modalRole === 'worker'
            ? (editingIndex >= 0 ? editingIndex : workerNodes.length) === 0
            : false;

      if (modalRole === 'master') {
        const nextMasters = [cloneDeep(values)];
        setMasterNodes(nextMasters);
        syncNodesToForm(nextMasters, workerNodes);
      } else if (editingIndex >= 0) {
        const next = [...workerNodes];
        next[editingIndex] = cloneDeep(values);
        setWorkerNodes(next);
        syncNodesToForm(masterNodes, next);
      } else {
        const next = [...workerNodes, cloneDeep(values)];
        setWorkerNodes(next);
        syncNodesToForm(masterNodes, next);
      }

      if (isPrimaryInferenceNode) {
        syncInferenceSystemArchitectureFromNode(form, values.system as Record<string, unknown> | undefined);
      }

      setNodeModalVisible(false);
      schedulePriceRefresh(form);
    },
    [editingIndex, form, masterNodes, modalRole, runMode, schedulePriceRefresh, syncNodesToForm, workerNodes],
  );

  const handleDeleteWorker = useCallback(
    (index: number) => {
      const next = workerNodes.filter((_, i) => i !== index);
      setWorkerNodes(next);
      syncNodesToForm(masterNodes, next);
      schedulePriceRefresh(form);
    },
    [form, masterNodes, schedulePriceRefresh, syncNodesToForm, workerNodes],
  );

  const closeNodeModal = useCallback(() => {
    setNodeModalVisible(false);
  }, []);

  const modalInitial = modalRole === 'master' ? masterNodes[0] : editingIndex >= 0 ? workerNodes[editingIndex] : undefined;
  const masterSpecForCard = isPrimarySecondary && !validateNodeGroupResource(masterNodes[0]) ? undefined : masterNodes[0];
  const workerModalReplicasMin = isEdit && modalInitial?.replicas === 0 ? 0 : 1;

  return {
    masterNodes,
    workerNodes,
    nodeModalVisible,
    modalRole,
    editingIndex,
    isNormal,
    isPrimarySecondary,
    modalInitial,
    masterSpecForCard,
    workerModalReplicasMin,
    handleRunModeChange,
    openMasterModal,
    openWorkerModal,
    handleNodeModalOk,
    handleDeleteWorker,
    closeNodeModal,
  };
}
